/*
 * Sección Almuerzos: botones separados "Inicio de almuerzo" y "Fin de almuerzo" (decisión del usuario)
 * con la lista de esa marca, el resumen del periodo y el promedio por persona (de quien demora menos a
 * quien demora más). Duración y "excedido" (más de 60 min) vienen del servidor (spec 4.4).
 */
import React, {useEffect, useState} from "react";
import StateView from "../components/StateView";
import SectionHeader from "../components/SectionHeader";
import StatTile from "../components/StatTile";
import StatusBadge from "../components/StatusBadge";
import EmployeeName from "../components/EmployeeName";
import Paginator from "../components/Paginator";
import {durationLabel, hhmm, lunchBadge, lunchByPerson, lunchList, lunchSummary, paginate} from "../utils/rows";
import {buildParams, shortDayLabel} from "../utils/period";

const MARKS = [
    {key: 'inicio', label: 'Inicio de almuerzo', field: 'inicio_almuerzo'},
    {key: 'fin', label: 'Fin de almuerzo', field: 'fin_almuerzo'},
];

const Almuerzos = ({rows, status, message, onRetry, filters, range, token, canExport, resetKey}) => {
    const [mark, setMark] = useState('inicio');
    const [page, setPage] = useState(1);
    useEffect(() => {
        setPage(1);
    }, [resetKey, mark]);

    const selected = MARKS.find((item) => item.key === mark);
    const list = lunchList(rows, mark);
    const summary = lunchSummary(rows);
    const people = lunchByPerson(rows);
    const current = paginate(list, page);
    const multiDay = range.desde !== range.hasta;
    const noLunchMarks = summary.inicios === 0 && summary.fines === 0;

    return (
        <section className="mc-section" aria-labelledby="mc-title-almuerzos">
            <SectionHeader id="mc-title-almuerzos" title="Almuerzos" canExport={canExport} token={token} range={range}
                           exportParams={buildParams(filters, range, {seccion: 'almuerzos'})}/>
            <StateView status={status} message={message} onRetry={onRetry} isEmpty={noLunchMarks}
                       emptyTitle="Sin almuerzos marcados en este rango">
                <div className="mc-tiles">
                    <StatTile label="Salieron a almorzar" value={summary.inicios}
                              hint={summary.enAlmuerzo ? `${summary.enAlmuerzo} en almuerzo ahora` : 'Marcas de inicio'}/>
                    <StatTile label="Regresaron" value={summary.fines} hint="Marcas de fin"/>
                    <StatTile label="Duración promedio" value={durationLabel(summary.promedio)} hint="Límite: 60 min"/>
                    <StatTile label="Más de 60 min" value={summary.excedidos} tone={summary.excedidos ? 'down' : 'neutral'}
                              hint={`${summary.sinAlmuerzo} jornadas sin almuerzo marcado`}/>
                </div>
                <div className="mc-toggle" role="group" aria-label="Marca de almuerzo">
                    {MARKS.map((item) => (
                        <button key={item.key} type="button" aria-pressed={mark === item.key}
                                className={`mc-toggle__item${mark === item.key ? ' is-active' : ''}`}
                                onClick={() => setMark(item.key)}>
                            {item.label}
                        </button>
                    ))}
                </div>
                <div className="mc-panel">
                    {list.length === 0 ? (
                        <p className="mc-inline-empty">Nadie marcó {selected.label.toLowerCase()} en este rango.</p>
                    ) : (
                        <ul className="mc-list">
                            {current.items.map((row) => {
                                const badge = lunchBadge(row, mark);
                                return (
                                    <li key={`${row.fecha}-${row.id_empleado}`} className="mc-list__item">
                                        <EmployeeName row={row}/>
                                        <span className="mc-list__time mc-num">
                                            {hhmm(row[selected.field])}
                                            {multiDay && <small>{shortDayLabel(row.fecha)}</small>}
                                        </span>
                                        <StatusBadge tone={badge.tone} text={badge.text}/>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                    <Paginator page={current.page} pages={current.pages} onChange={setPage}/>
                </div>
                {people.length > 0 &&
                    <div className="mc-panel">
                        <h3 className="mc-panel__title">Promedio por persona</h3>
                        <ol className="mc-list mc-list--ranked">
                            {people.map((person) => (
                                <li key={person.id_empleado} className="mc-list__item">
                                    <EmployeeName row={person}/>
                                    <span className="mc-list__time mc-num">{durationLabel(person.promedio)}</span>
                                    <StatusBadge tone={person.excedidos ? 'down' : 'muted'}
                                                 text={`${person.dias} ${person.dias === 1 ? 'día' : 'días'}${person.excedidos ? ` · ${person.excedidos} excedido${person.excedidos === 1 ? '' : 's'}` : ''}`}/>
                                </li>
                            ))}
                        </ol>
                    </div>}
            </StateView>
        </section>
    );
};

export default Almuerzos;
